/**
 * src/modules/asystent-kontekst.ts — pośrednik między widokami spraw a dymkiem asystenta (S-176, 13.10.2026).
 *
 * Widok, który pokazuje sprawę albo rozmowę z kupującym (szuflada reklamacji/dyskusji/zwrotu, wątek Wiadomości,
 * szuflada Obsługi klienta, czat w karcie zamówienia), woła `ustawKontekstAsystenta(zrodlo, {...})` przy otwarciu
 * i `ustawKontekstAsystenta(zrodlo, null)` przy zamknięciu. Dymek (`asystent-dock.ts`) bierze najświeższy kontekst.
 * Ostatnie konteksty leżą też w `window.__sbAsystentKonteksty` — dymek startuje z opóźnieniem i nie zgubi sprawy
 * otwartej przed swoim startem.
 *
 * Po udanej wysyłce odpowiedzi widok woła `zglosWysylkeAsystentowi({tekst, klucz?})` — tylko do śladu i nauki
 * asystenta (treść wysłana vs propozycja). Asystent niczego nie wysyła.
 */

export type KontekstAsystenta = {
  kanal: string;            // 'allegro_issue' | 'allegro_thread' | 'allegro_return' | 'prestashop' | 'ebay' | 'sms'
  klucz: string;            // ASCASE_<id> | THREAD_<konto>_<wątek> | PSMSG_… | EBMSG_…
  account_id?: number | string;
  order_id?: string;
  tytul?: string;
  /** Element widoku sprawy (szuflada, panel czatu, wątek). Dymek pomija kontekst, gdy widok jest schowany,
   * a „Wstaw” szuka pola odpowiedzi tylko w nim (przegląd 13.10). */
  kontener?: Element | null;
};

/** Czy widok sprawy jest na ekranie: podpięty, bez display:none / visibility:hidden, w oknie (zamknięte szuflady
 * zjeżdżają za prawą krawędź). Bez kontenera — nie umiemy sprawdzić, kontekst zostaje. */
export function kontenerWidoczny(el: Element | null | undefined): boolean {
  if (!el) return true;
  if (!(el as any).isConnected) return false;
  try {
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    const r = el.getBoundingClientRect();
    const szer = window.innerWidth || 0;
    const wys = window.innerHeight || 0;
    if (r.width < 2 || r.height < 2) return false;
    if (szer && (r.left >= szer - 1 || r.right <= 1)) return false;
    if (wys && (r.top >= wys - 1 || r.bottom <= 1)) return false;
  } catch (_) { /* bez layoutu — zostaje */ }
  return true;
}

export const ZDARZENIE_KONTEKSTU = 'sb-asystent-kontekst';

export function ustawKontekstAsystenta(zrodlo: string, ctx: KontekstAsystenta | null): void {
  if (typeof window === 'undefined') return;
  try {
    const w = window as any;
    if (!w.__sbAsystentKonteksty) w.__sbAsystentKonteksty = {};
    if (ctx && ctx.klucz) w.__sbAsystentKonteksty[zrodlo] = Object.assign({}, ctx, { zrodlo, at: Date.now() });
    else delete w.__sbAsystentKonteksty[zrodlo];
    window.dispatchEvent(new CustomEvent(ZDARZENIE_KONTEKSTU, { detail: ctx && ctx.klucz ? Object.assign({ zrodlo }, ctx) : { zrodlo, kanal: null } }));
  } catch (_) { /* dymek to dodatek — błąd nie może zatrzymać widoku */ }
}

if (typeof window !== 'undefined') {
  // dla routera (bez importów modułów widoków): zdjęcie szuflady sprawy przy zmianie trasy czyści jej kontekst
  (window as any).__sbAsystentWyczysc = (zrodlo: string) => ustawKontekstAsystenta(zrodlo, null);
}

export function zglosWysylkeAsystentowi(dane: { tekst: string; klucz?: string; message_id?: string | null; kanal?: string }): void {
  try {
    const a = (window as any).__sbAsystent;
    if (a && typeof a.poWysylce === 'function') a.poWysylce(dane);
  } catch (_) { /* jw. */ }
}

export function kluczWatku(accountId: number | string, threadId: string): string {
  return 'THREAD_' + String(accountId || 0) + '_' + String(threadId || '');
}
